import React, { useState, useEffect } from 'react';
import { X, Building2, Users, Briefcase, HardDrive, Activity, AlertCircle, Download, UserPlus, Settings, Loader2, ArrowRightLeft, Trash2, Archive } from 'lucide-react';
import { getCompanyUsers, getCompanyJobs, transferCompanyOwnership, checkUserByEmail, transferOwnershipToExistingUser, transferOwnershipToNewUser } from '../../../../services/companyService';
import { auditLogService } from '../../../../services/auditLogService';
import DeletionPreviewModal from './DeletionPreviewModal';

const CompanyDetailsModal = ({ company, onClose, onSuspend, onRefresh }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Real data state
  const [owners, setOwners] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [personnelSubTab, setPersonnelSubTab] = useState('all');

  // Action states
  const [transferLoading, setTransferLoading] = useState(false);
  const [selectedNewOwner, setSelectedNewOwner] = useState('');
  const [actionError, setActionError] = useState(null);
  const [actionSuccess, setActionSuccess] = useState(null);
  const [showDeletionPreview, setShowDeletionPreview] = useState(false);

  // Enhanced transfer ownership states
  const [transferMode, setTransferMode] = useState('existing'); // 'existing' | 'new'
  const [newOwnerEmail, setNewOwnerEmail] = useState('');
  const [newOwnerName, setNewOwnerName] = useState('');
  const [newOwnerPassword, setNewOwnerPassword] = useState('');
  const [emailCheckLoading, setEmailCheckLoading] = useState(false);
  const [emailCheckResult, setEmailCheckResult] = useState(null); // null | { exists, inCompany, userId, userName }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Activity },
    { id: 'owners', label: 'Owners', icon: Users },
    { id: 'personnel', label: 'Personnel', icon: UserPlus },
    { id: 'jobs', label: 'Jobs', icon: Briefcase },
    { id: 'audit', label: 'Logs', icon: Settings },
    { id: 'actions', label: 'Actions', icon: AlertCircle }
  ];

  // Load initial data on mount (for overview stats)
  useEffect(() => {
    const loadInitialData = async () => {
      if (!company?.id) return;

      try {
        // Load users for owner count
        const usersResult = await getCompanyUsers(company.id);
        if (usersResult.success) {
          const allUsers = usersResult.data || [];
          setOwners(allUsers.filter(u => u.role === 'primary_owner' || u.role === 'owner'));
          setPersonnel(allUsers);
        }

        // Load audit logs count
        try {
          const logs = await auditLogService.getAll({ companyId: company.id });
          const companyLogs = logs?.filter(log => log.companyId === company.id) || [];
          setAuditLogs(companyLogs.slice(0, 50));
        } catch {
          setAuditLogs([]);
        }
      } catch (err) {
        console.error('Failed to load initial data:', err);
      }
    };

    loadInitialData();
  }, [company?.id]);

  // Load tab-specific data when tab changes
  useEffect(() => {
    const loadTabData = async () => {
      if (!company?.id) return;

      // Skip if data already loaded or on overview tab
      if (activeTab === 'overview') return;

      setLoading(true);
      setError(null);

      try {
        if ((activeTab === 'owners' || activeTab === 'personnel') && personnel.length === 0) {
          const result = await getCompanyUsers(company.id);
          if (result.success) {
            const allUsers = result.data || [];
            setOwners(allUsers.filter(u => u.role === 'primary_owner' || u.role === 'owner'));
            setPersonnel(allUsers);
          }
        } else if (activeTab === 'jobs' && jobs.length === 0) {
          const result = await getCompanyJobs(company.id);
          if (result.success) {
            setJobs(result.data || []);
          }
        } else if (activeTab === 'audit' && auditLogs.length === 0) {
          try {
            const logs = await auditLogService.getAll({ companyId: company.id });
            const companyLogs = logs?.filter(log => log.companyId === company.id) || [];
            setAuditLogs(companyLogs.slice(0, 50));
          } catch {
            setAuditLogs([]);
          }
        }
      } catch (err) {
        setError(err?.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadTabData();
  }, [activeTab, company?.id, personnel.length, jobs.length, auditLogs.length]);

  const renderLoading = () => (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="w-6 h-6 text-orange-400 animate-spin" />
      <span className="ml-2 text-text-secondary">Loading...</span>
    </div>
  );

  const renderError = () => (
    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-center">
      <p className="text-red-400">{error}</p>
    </div>
  );

  const renderEmptyState = (message) => (
    <div className="text-center py-12">
      <p className="text-text-secondary">{message}</p>
    </div>
  );

  // Helper to get company name (handles both property naming conventions)
  const getCompanyName = () => company?.companyName || company?.name || 'Unknown';
  const getCompanyTag = () => company?.companyTag || company?.tag || '';

  // Get owner - from company.primaryOwner (via foreign key) or from loaded owners array (fallback)
  const getPrimaryOwner = () => {
    // First try from company props (populated via primary_owner_id foreign key)
    if (company?.primaryOwner?.fullName || company?.primaryOwner?.name) {
      return {
        name: company?.primaryOwner?.fullName || company?.primaryOwner?.name,
        email: company?.primaryOwner?.email
      };
    }
    // Fallback: look for primary_owner in loaded owners array
    const primaryOwner = owners.find(o => o.role === 'primary_owner');
    if (primaryOwner) {
      return {
        name: primaryOwner.user?.full_name || 'Unknown',
        email: primaryOwner.user?.email || ''
      };
    }
    // Last resort: first owner
    if (owners.length > 0) {
      return {
        name: owners[0].user?.full_name || 'Unknown',
        email: owners[0].user?.email || ''
      };
    }
    return { name: 'Unknown', email: '' };
  };

  const getCreatedDate = () => company?.createdAt || company?.createdDate;

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Company Metadata */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-text-primary font-semibold mb-4 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-orange-400" />
          Company Information
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-text-secondary text-sm">Company Name</label>
            <p className="text-text-primary mt-1">{getCompanyName()}</p>
          </div>
          <div>
            <label className="text-text-secondary text-sm">Company Tag</label>
            <p className="text-text-primary mt-1">{getCompanyTag()}</p>
          </div>
          <div>
            <label className="text-text-secondary text-sm">Primary Owner</label>
            <p className="text-text-primary mt-1">{getPrimaryOwner().name}</p>
            <p className="text-text-secondary text-xs">{getPrimaryOwner().email}</p>
          </div>
          <div>
            <label className="text-text-secondary text-sm">Status</label>
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border mt-1 ${
              company?.status === 'active'
                ? 'bg-green-500/10 text-green-400 border-green-500/20'
                : company?.status === 'suspended'
                ? 'bg-red-500/10 text-red-400 border-red-500/20'
                : 'bg-gray-500/10 text-gray-400 border-gray-500/20'
            }`}>
              {company?.status?.charAt(0)?.toUpperCase() + company?.status?.slice(1)}
            </span>
          </div>
          <div>
            <label className="text-text-secondary text-sm">Created Date</label>
            <p className="text-text-primary mt-1">
              {getCreatedDate() ? new Date(getCreatedDate()).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) : 'N/A'}
            </p>
          </div>
          <div>
            <label className="text-text-secondary text-sm">Last Updated</label>
            <p className="text-text-primary mt-1">
              {company?.updatedAt ? new Date(company.updatedAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) : 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-surface border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <Users className="w-5 h-5 text-blue-400" />
            <span className="text-2xl font-bold text-text-primary">{company?.userCount || 0}</span>
          </div>
          <p className="text-text-secondary text-sm">Total Users</p>
        </div>
        <div className="bg-surface border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <Briefcase className="w-5 h-5 text-green-400" />
            <span className="text-2xl font-bold text-text-primary">{company?.jobCount || 0}</span>
          </div>
          <p className="text-text-secondary text-sm">Total Jobs</p>
        </div>
        <div className="bg-surface border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <UserPlus className="w-5 h-5 text-purple-400" />
            <span className="text-2xl font-bold text-text-primary">{owners.length || '-'}</span>
          </div>
          <p className="text-text-secondary text-sm">Owners</p>
        </div>
        <div className="bg-surface border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <Activity className="w-5 h-5 text-orange-400" />
            <span className="text-2xl font-bold text-text-primary">{auditLogs.length || '-'}</span>
          </div>
          <p className="text-text-secondary text-sm">Recent Activities</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-text-primary font-semibold mb-4">Quick Actions</h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setActiveTab('personnel')}
            className="px-4 py-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 rounded-lg text-sm font-medium transition-colors"
          >
            View Personnel
          </button>
          <button
            onClick={() => setActiveTab('jobs')}
            className="px-4 py-2 bg-green-500/10 hover:bg-green-500/20 text-green-400 rounded-lg text-sm font-medium transition-colors"
          >
            View Jobs
          </button>
          <button
            onClick={() => setActiveTab('audit')}
            className="px-4 py-2 bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 rounded-lg text-sm font-medium transition-colors"
          >
            View Logs
          </button>
        </div>
      </div>
    </div>
  );

  const renderOwnersTab = () => {
    if (loading) return renderLoading();
    if (error) return renderError();
    if (owners.length === 0) return renderEmptyState('No owners found for this company.');

    return (
      <div className="space-y-4">
        <p className="text-text-secondary text-sm">Company owners with administrative access.</p>
        <div className="space-y-3">
          {owners.map((owner) => (
            <div key={owner.id} className="bg-surface border border-border rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white font-semibold">
                  {owner.user?.full_name?.split(' ')?.map(n => n?.[0])?.join('') || '?'}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-text-primary font-medium">{owner.user?.full_name || 'Unknown'}</span>
                    {owner.role === 'primary_owner' && (
                      <span className="px-2 py-0.5 bg-orange-500/10 text-orange-400 text-xs rounded border border-orange-500/20">
                        Primary
                      </span>
                    )}
                  </div>
                  <p className="text-text-secondary text-sm">{owner.user?.email}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderPersonnelTab = () => {
    if (loading) return renderLoading();
    if (error) return renderError();
    if (personnel.length === 0) return renderEmptyState('No personnel found for this company.');

    const employees = personnel.filter(p => p.role !== 'contractor');
    const contractors = personnel.filter(p => p.role === 'contractor');
    const inactiveCount = personnel.filter(p => !p.is_active).length;

    // Filter based on selected sub-tab
    const filteredPersonnel = personnelSubTab === 'all'
      ? personnel
      : personnelSubTab === 'employees'
        ? employees
        : contractors;

    // Helper to render tab buttons
    const renderTabButtons = () => (
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setPersonnelSubTab('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
            personnelSubTab === 'all'
              ? 'bg-orange-500/10 text-orange-400'
              : 'text-text-secondary hover:text-text-primary hover:bg-surface'
          }`}
        >
          All Personnel ({personnel.length})
          {inactiveCount > 0 && (
            <span className="ml-1 text-xs text-text-secondary">({inactiveCount} inactive)</span>
          )}
        </button>
        <button
          onClick={() => setPersonnelSubTab('employees')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
            personnelSubTab === 'employees'
              ? 'bg-orange-500/10 text-orange-400'
              : 'text-text-secondary hover:text-text-primary hover:bg-surface'
          }`}
        >
          Employees ({employees.length})
        </button>
        <button
          onClick={() => setPersonnelSubTab('contractors')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
            personnelSubTab === 'contractors'
              ? 'bg-purple-500/10 text-purple-400'
              : 'text-text-secondary hover:text-text-primary hover:bg-surface'
          }`}
        >
          Contractors ({contractors.length})
        </button>
      </div>
    );

    // Handle empty state for filtered results
    if (filteredPersonnel.length === 0) {
      const tabLabel = personnelSubTab === 'all' ? 'personnel' : personnelSubTab;
      return (
        <div className="space-y-4">
          {renderTabButtons()}
          {renderEmptyState(`No ${tabLabel} found for this company.`)}
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {renderTabButtons()}
        <div className="bg-surface border border-border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Role</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Alias</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredPersonnel.map((person) => (
                <tr
                  key={person.id}
                  className={`hover:bg-background ${!person.is_active ? 'opacity-50' : ''}`}
                >
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-text-primary text-sm font-medium">{person.user?.full_name || 'Unknown'}</p>
                      <p className="text-text-secondary text-xs">{person.user?.email}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded border capitalize ${
                      person.role === 'contractor'
                        ? 'bg-purple-500/10 text-purple-400 border-purple-500/20'
                        : person.role === 'primary_owner' || person.role === 'owner'
                        ? 'bg-orange-500/10 text-orange-400 border-orange-500/20'
                        : 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                    }`}>
                      {person.role?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded border ${
                      person.is_active
                        ? 'bg-green-500/10 text-green-400 border-green-500/20'
                        : 'bg-red-500/10 text-red-400 border-red-500/20'
                    }`}>
                      {person.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-text-secondary text-sm">
                    {person.company_alias || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderJobsTab = () => {
    if (loading) return renderLoading();
    if (error) return renderError();
    if (jobs.length === 0) return renderEmptyState('No jobs found for this company.');

    return (
      <div className="space-y-4">
        <div className="bg-surface border border-border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Job Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {jobs.map((job) => (
                <tr key={job.id} className="hover:bg-background">
                  <td className="px-4 py-3 text-text-primary text-sm font-medium">{job.job_name}</td>
                  <td className="px-4 py-3 text-text-secondary text-sm capitalize">{job.job_type?.replace('_', ' ') || '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded border capitalize ${
                      job.status === 'active'
                        ? 'bg-green-500/10 text-green-400 border-green-500/20'
                        : job.status === 'paused'
                        ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'
                        : 'bg-gray-500/10 text-gray-400 border-gray-500/20'
                    }`}>
                      {job.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-text-secondary text-sm">
                    {new Date(job.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderAuditTab = () => {
    if (loading) return renderLoading();
    if (error) return renderError();
    if (auditLogs.length === 0) return renderEmptyState('No audit logs found for this company.');

    return (
      <div className="space-y-4">
        <div className="bg-surface border border-border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Timestamp</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Actor</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Action</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Category</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {auditLogs.map((log) => (
                <tr key={log.id} className="hover:bg-background">
                  <td className="px-4 py-3 text-text-secondary text-sm">
                    {new Date(log.timestamp).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-text-primary text-sm">{log.actorEmail}</td>
                  <td className="px-4 py-3 text-text-secondary text-sm">{log.action}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 bg-purple-500/10 text-purple-400 text-xs rounded border border-purple-500/20">
                      {log.eventCategory}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Handle transfer ownership
  const handleTransferOwnership = async () => {
    if (!selectedNewOwner) {
      setActionError('Please select a new owner');
      return;
    }

    setTransferLoading(true);
    setActionError(null);
    setActionSuccess(null);

    try {
      const result = await transferCompanyOwnership(company.id, selectedNewOwner);
      if (result.success) {
        setActionSuccess('Ownership transferred successfully');
        setSelectedNewOwner('');
        // Refresh data
        if (onRefresh) onRefresh();
      } else {
        setActionError(result.error || 'Failed to transfer ownership');
      }
    } catch (err) {
      setActionError(err?.message || 'An error occurred');
    } finally {
      setTransferLoading(false);
    }
  };

  // Get eligible owners for transfer (all personnel except current primary owner)
  const getEligibleOwners = () => {
    const primaryOwner = owners.find(o => o.role === 'primary_owner');
    return personnel.filter(p => p.user_id !== primaryOwner?.user_id);
  };

  // Handle email check for new owner
  const handleCheckEmail = async () => {
    if (!newOwnerEmail?.trim()) {
      setActionError('Please enter an email address');
      return;
    }

    setEmailCheckLoading(true);
    setActionError(null);
    setEmailCheckResult(null);

    try {
      const result = await checkUserByEmail(newOwnerEmail, company.id);

      if (!result.success) {
        setActionError(result.error || 'Failed to check email');
        return;
      }

      if (result.exists && result.inCompany) {
        setActionError('This user is already in the company. Use the "Existing User" tab instead.');
        return;
      }

      setEmailCheckResult(result);
    } catch (err) {
      setActionError(err?.message || 'Failed to check email');
    } finally {
      setEmailCheckLoading(false);
    }
  };

  // Handle transfer to new email (existing Mervo user or create new)
  const handleTransferToNewEmail = async () => {
    if (!emailCheckResult) {
      setActionError('Please check the email first');
      return;
    }

    setTransferLoading(true);
    setActionError(null);
    setActionSuccess(null);

    try {
      let result;

      if (emailCheckResult.exists) {
        // User exists in Mervo but not in this company - add and transfer
        result = await transferOwnershipToExistingUser(company.id, emailCheckResult.userId, getCompanyTag());
      } else {
        // New user - validate and create
        if (!newOwnerName?.trim()) {
          setActionError('Please enter the new owner\'s name');
          setTransferLoading(false);
          return;
        }
        if (!newOwnerPassword || newOwnerPassword.length < 8) {
          setActionError('Password must be at least 8 characters');
          setTransferLoading(false);
          return;
        }
        result = await transferOwnershipToNewUser(company.id, getCompanyTag(), newOwnerEmail, newOwnerName, newOwnerPassword);
      }

      if (result.success) {
        setActionSuccess(result.message || 'Ownership transferred successfully');
        // Reset form
        setNewOwnerEmail('');
        setNewOwnerName('');
        setNewOwnerPassword('');
        setEmailCheckResult(null);
        setTransferMode('existing');
        if (onRefresh) onRefresh();
      } else {
        setActionError(result.error || 'Failed to transfer ownership');
      }
    } catch (err) {
      setActionError(err?.message || 'An error occurred');
    } finally {
      setTransferLoading(false);
    }
  };

  // Reset new email form when switching modes
  const handleTransferModeChange = (mode) => {
    setTransferMode(mode);
    setActionError(null);
    setActionSuccess(null);
    if (mode === 'new') {
      setSelectedNewOwner('');
    } else {
      setNewOwnerEmail('');
      setNewOwnerName('');
      setNewOwnerPassword('');
      setEmailCheckResult(null);
    }
  };

  const renderActionsTab = () => (
    <div className="space-y-4">
      <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-yellow-400 font-medium mb-1">Warning</p>
          <p className="text-text-secondary text-sm">These actions have significant impact on the company and its users. Proceed with caution.</p>
        </div>
      </div>

      {/* Action feedback */}
      {actionError && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
          <p className="text-red-400 text-sm">{actionError}</p>
        </div>
      )}
      {actionSuccess && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
          <p className="text-green-400 text-sm">{actionSuccess}</p>
        </div>
      )}

      <div className="space-y-3">
        {/* Suspend Company */}
        <div className="bg-surface border border-border rounded-lg p-6">
          <h3 className="text-text-primary font-semibold mb-2">Suspend Company</h3>
          <p className="text-text-secondary text-sm mb-4">Temporarily disable company access. Users will not be able to login.</p>
          <button
            onClick={onSuspend}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition-colors"
          >
            Suspend Company
          </button>
        </div>

        {/* Transfer Ownership */}
        <div className="bg-surface border border-border rounded-lg p-6">
          <div className="flex items-center gap-2 mb-2">
            <ArrowRightLeft className="w-5 h-5 text-orange-400" />
            <h3 className="text-text-primary font-semibold">Transfer Ownership</h3>
          </div>
          <p className="text-text-secondary text-sm mb-4">
            Transfer primary ownership to another user. The current owner will be demoted to a regular owner.
          </p>

          {/* Transfer Mode Tabs */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => handleTransferModeChange('existing')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                transferMode === 'existing'
                  ? 'bg-orange-500/10 text-orange-400 border border-orange-500/30'
                  : 'bg-background text-text-secondary hover:bg-surface border border-border'
              }`}
            >
              Existing User
            </button>
            <button
              onClick={() => handleTransferModeChange('new')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                transferMode === 'new'
                  ? 'bg-orange-500/10 text-orange-400 border border-orange-500/30'
                  : 'bg-background text-text-secondary hover:bg-surface border border-border'
              }`}
            >
              New Email
            </button>
          </div>

          {transferMode === 'existing' ? (
            /* Existing User Dropdown */
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row gap-3">
                <select
                  value={selectedNewOwner}
                  onChange={(e) => setSelectedNewOwner(e.target.value)}
                  className="flex-1 px-4 py-2 bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                >
                  <option value="">Select new owner...</option>
                  {getEligibleOwners().map((user) => (
                    <option key={user.user_id} value={user.user_id}>
                      {user.user?.full_name || 'Unknown'} ({user.user?.email}) - {user.role?.replace('_', ' ')}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleTransferOwnership}
                  disabled={transferLoading || !selectedNewOwner}
                  className="px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/50 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                >
                  {transferLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Transferring...
                    </>
                  ) : (
                    'Transfer Ownership'
                  )}
                </button>
              </div>
              {getEligibleOwners().length === 0 && (
                <p className="text-text-tertiary text-xs">
                  No users in this company. Use "New Email" to transfer to someone outside the company.
                </p>
              )}
            </div>
          ) : (
            /* New Email Form */
            <div className="space-y-3">
              {/* Email Input with Check Button */}
              <div className="flex gap-2">
                <input
                  type="email"
                  value={newOwnerEmail}
                  onChange={(e) => {
                    setNewOwnerEmail(e.target.value);
                    setEmailCheckResult(null);
                  }}
                  placeholder="Enter email address..."
                  className="flex-1 px-4 py-2 bg-background border border-border rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                />
                <button
                  onClick={handleCheckEmail}
                  disabled={emailCheckLoading || !newOwnerEmail?.trim()}
                  className="px-4 py-2 bg-blue-500/10 hover:bg-blue-500/20 disabled:bg-blue-500/5 text-blue-400 disabled:text-blue-400/50 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                >
                  {emailCheckLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Checking...
                    </>
                  ) : (
                    'Check Email'
                  )}
                </button>
              </div>

              {/* Email Check Result - User exists in Mervo but not in company */}
              {emailCheckResult?.exists && !emailCheckResult.inCompany && (
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                  <p className="text-yellow-400 font-medium text-sm mb-1">User Found in Mervo</p>
                  <p className="text-text-secondary text-sm mb-3">
                    <strong>{emailCheckResult.userName}</strong> ({emailCheckResult.userEmail}) exists in Mervo but is not part of this company.
                    They will be added as the new primary owner.
                  </p>
                  <button
                    onClick={handleTransferToNewEmail}
                    disabled={transferLoading}
                    className="px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/50 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                  >
                    {transferLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Transferring...
                      </>
                    ) : (
                      'Add & Transfer Ownership'
                    )}
                  </button>
                </div>
              )}

              {/* Email Check Result - New User (not in Mervo) */}
              {emailCheckResult && !emailCheckResult.exists && (
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 space-y-3">
                  <p className="text-blue-400 font-medium text-sm">New User - Create Account</p>
                  <p className="text-text-secondary text-sm">
                    This email is not registered in Mervo. Fill in the details below to create an account and transfer ownership.
                  </p>
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={newOwnerName}
                      onChange={(e) => setNewOwnerName(e.target.value)}
                      placeholder="Full Name"
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                    />
                    <input
                      type="password"
                      value={newOwnerPassword}
                      onChange={(e) => setNewOwnerPassword(e.target.value)}
                      placeholder="Temporary Password (min 8 characters)"
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                    />
                    <p className="text-text-tertiary text-xs">
                      The new owner will use this password to log in for the first time.
                    </p>
                  </div>
                  <button
                    onClick={handleTransferToNewEmail}
                    disabled={transferLoading || !newOwnerName?.trim() || !newOwnerPassword || newOwnerPassword.length < 8}
                    className="px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/50 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                  >
                    {transferLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Creating & Transferring...
                      </>
                    ) : (
                      'Create Account & Transfer'
                    )}
                  </button>
                </div>
              )}

              {!emailCheckResult && (
                <p className="text-text-tertiary text-xs">
                  Enter an email address and click "Check Email" to verify if the user exists.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Archive/Delete Company */}
        <div className="bg-surface border border-red-500/30 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-2">
            <Archive className="w-5 h-5 text-red-400" />
            <h3 className="text-red-400 font-semibold">Archive or Delete Company</h3>
          </div>
          <p className="text-text-secondary text-sm mb-4">
            Archive this company to disable access while preserving data for 30 days.
            After the grace period, you can permanently delete all data.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => setShowDeletionPreview(true)}
              className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Preview & Delete
            </button>
          </div>
          <p className="text-text-tertiary text-xs mt-3">
            This will show you exactly what data will be affected before any action is taken.
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-background border border-border rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-text-primary">{getCompanyName()}</h2>
              <p className="text-text-secondary text-sm">{getCompanyTag()}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-text-secondary" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 px-6 pt-4 border-b border-border overflow-x-auto">
          {tabs.map((tab) => {
            const IconComponent = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-orange-500/10 text-orange-400 border-b-2 border-orange-500'
                    : 'text-text-secondary hover:text-text-primary hover:bg-surface'
                }`}
              >
                <IconComponent className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'overview' && renderOverviewTab()}
          {activeTab === 'owners' && renderOwnersTab()}
          {activeTab === 'personnel' && renderPersonnelTab()}
          {activeTab === 'jobs' && renderJobsTab()}
          {activeTab === 'audit' && renderAuditTab()}
          {activeTab === 'actions' && renderActionsTab()}
        </div>
      </div>

      {/* Deletion Preview Modal */}
      {showDeletionPreview && (
        <DeletionPreviewModal
          company={company}
          onClose={() => setShowDeletionPreview(false)}
          onSuccess={() => {
            setShowDeletionPreview(false);
            if (onRefresh) onRefresh();
            onClose();
          }}
        />
      )}
    </div>
  );
};

export default CompanyDetailsModal;
